import { Modal, Spin, Table, Tag } from 'antd';
import React, { useEffect } from 'react';
import { Endpoints } from '../constants/endpoint.constant';
import { useGet } from '../hooks/useRest.hook';
import { getInfringementActionLabel } from '../services/firestoreTracking.service';
import { formatDateTimeZA } from '../utils/date.util';

const formatDateTime = value => {
    return formatDateTimeZA(value);
};

const formatSpeed = value => {
    const number = Number(value);
    return Number.isFinite(number) ? `${number.toFixed(1)} km/h` : 'Not available';
};

const formatDuration = value => {
    const number = Number(value);
    return Number.isFinite(number) ? `${number}s` : 'Not available';
};

const formatStatus = value => {
    if (!value) return 'None';
    return value
        .toString()
        .split('_')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
};

const formatType = record => {
    if (record?.type === 'tracking_outage') {
        return record?.category ? formatStatus(record.category) : 'Tracking Outage';
    }
    return formatStatus(record?.type);
};

const LocationEvidence = ({ record }) => {
    const coordinates = record?.peakCoordinates || record?.startCoordinates || 'Not available';
    const mapsLink = record?.peakMapsLink || record?.startMapsLink || null;

    return <div className="flex flex-col gap-1">
        <span className="break-all">{coordinates}</span>
        {mapsLink && <a href={mapsLink} target="_blank" rel="noreferrer" className="text-primary-orange underline underline-offset-2">Open in Google Maps</a>}
    </div>;
};

export const DriverInfringementsModal = ({ driver, open, onCancel }) => {
    const infringementsRq = useGet(Endpoints.GET_DRIVER_INFRINGEMENTS(driver?.id), false);
    const loadInfringements = infringementsRq?.load;

    useEffect(() => {
        if (open && driver?.id) {
            loadInfringements?.();
        }
    }, [open, driver?.id, loadInfringements]);

    return <Modal width={1100} title={`Infringements - ${driver?.user?.name || 'Driver'}`} open={open} footer={null} onCancel={onCancel}>
        {infringementsRq?.loading ? <div className="self-center py-10 flex justify-center"><Spin /></div> : <Table
            rowKey={record => record?.id}
            pagination={{ pageSize: 10 }}
            dataSource={Array.isArray(infringementsRq?.data) ? infringementsRq.data : []}
            locale={{ emptyText: 'No infringement history found.' }}
            columns={[
                {
                    title: 'Date',
                    dataIndex: 'startTime',
                    render: text => formatDateTime(text),
                },
                {
                    title: 'Trip',
                    dataIndex: 'tripOrderId',
                    render: (text) => <a href={`/active-trip-orders#TRIP-CARD-${text}`}>MYC#{text}</a>,
                },
                {
                    title: 'Type',
                    dataIndex: 'type',
                    render: (_, record) => formatType(record),
                },
                {
                    title: 'Max Speed',
                    dataIndex: 'maxSpeedKph',
                    render: value => formatSpeed(value),
                },
                {
                    title: 'Limit',
                    dataIndex: 'roadSpeedLimitKph',
                    render: value => formatSpeed(value),
                },
                {
                    title: 'Duration',
                    dataIndex: 'durationOverLimitSeconds',
                    render: (_, record) => formatDuration(record?.durationOverLimitSeconds ?? record?.outageDurationSeconds),
                },
                {
                    title: 'Status',
                    dataIndex: 'status',
                    render: value => <Tag color={value === 'confirmed' ? 'red' : value === 'dismissed' ? 'default' : 'orange'}>{formatStatus(value)}</Tag>,
                },
                {
                    title: 'Confidence',
                    dataIndex: 'confidence',
                    render: value => Number(value || 0).toFixed(2),
                },
                {
                    title: 'Latest Action',
                    dataIndex: 'latestActionType',
                    render: (_, record) => record?.latestActionType ? getInfringementActionLabel(record.latestActionType) : 'No action logged',
                },
                {
                    title: 'Manager Note',
                    dataIndex: 'latestActionNote',
                    render: value => value || 'No note',
                },
                {
                    title: 'Location',
                    dataIndex: 'peakCoordinates',
                    render: (_, record) => <LocationEvidence record={record} />,
                },
            ]}
        />}
    </Modal>
}
